/**
 * Document libraries: the shapes shared by the store, the access and lifecycle
 * rules, the library browser, the form element that embeds a library, and the
 * workflow step that files documents into one.
 *
 * Tables: supabase/migrations/00512_document_libraries.sql.
 */

export type LibraryRole = 'viewer' | 'contributor' | 'manager'

export const LIBRARY_ROLES: LibraryRole[] = ['viewer', 'contributor', 'manager']

export const LIBRARY_ROLE_LABEL: Record<LibraryRole, string> = {
  viewer: 'Can view',
  contributor: 'Can add and update',
  manager: 'Can manage',
}

export const LIBRARY_ROLE_HELP: Record<LibraryRole, string> = {
  viewer: 'Open and download documents.',
  contributor: 'Everything a viewer can do, plus add documents, upload new versions and create folders.',
  manager: 'Everything a contributor can do, plus rename, move, delete, and change who has access.',
}

/** The principal that means "everyone signed in". */
export const EVERYONE = '*'

/** One line of an access list. `principal` is a person's name, a People Group
 *  written "@Group", or "*" for everyone signed in. */
export interface AccessRule {
  principal: string
  role: LibraryRole
}

export type StatusColor = 'gray' | 'blue' | 'amber' | 'green' | 'purple' | 'red'

/** One status in a library's lifecycle. */
export interface LibraryStatus {
  key: string
  label: string
  color: StatusColor
  /** The status a new document starts in. Exactly one. */
  initial?: boolean
  /** The document is the approved, current one that people should use. */
  released?: boolean
  /** No longer for use (superseded, obsolete). Hidden from the list by default. */
  retired?: boolean
  /** In a controlled library, moving a document INTO this status goes through
   *  the governing workflow first. */
  controlled?: boolean
}

/** The lifecycle a new library starts with. Mirrors ordinary document control:
 *  a draft is reviewed, approved for use, then superseded by its replacement or
 *  withdrawn. */
export const DEFAULT_STATUSES: LibraryStatus[] = [
  { key: 'draft', label: 'Draft', color: 'gray', initial: true },
  { key: 'in_review', label: 'In review', color: 'amber' },
  { key: 'approved', label: 'Approved', color: 'green', released: true, controlled: true },
  { key: 'superseded', label: 'Superseded', color: 'purple', retired: true },
  { key: 'obsolete', label: 'Obsolete', color: 'red', retired: true, controlled: true },
]

export interface DocumentLibrary {
  id: string
  name: string
  description: string | null
  statuses: LibraryStatus[]
  /** Changes go through the governing workflow before they take effect. */
  controlled: boolean
  /** workflow_drafts.id of the workflow whose live version governs changes. */
  governingWorkflowId: string | null
  /** Access at the library root. */
  permissions: AccessRule[]
  /** Lower-case extensions without the dot; empty = any. */
  allowedExtensions: string[]
  maxSizeMb: number | null
  /** Document control settings (revision scheme, numbering, types, review
   *  defaults). Normalized by documentControl.normalizeLibraryControl. */
  control?: LibraryControl
  createdBy: string | null
  createdAt: string
  updatedAt: string
  archivedAt: string | null
}

// ---------------------------------------------------------------------------
// Document control (00020_document_control.sql, documentControl.ts)
// ---------------------------------------------------------------------------

/** alpha: A, B ... Z, AA. numeric: 1, 2, 3. dash_alpha: -, A, B ... */
export type RevisionStyle = 'alpha' | 'numeric' | 'dash_alpha'

export interface RevisionScheme {
  style: RevisionStyle
  /** Working versions carry a minor number (B.1, B.2) and the release takes the major (B). */
  majorMinor?: boolean
  /** Skip I, O, Q, S, X and Z in letter revisions (ASME Y14.35 practice). */
  skipLetters?: boolean
}

/** One entry of a library's document type list. */
export interface DocumentTypeDef {
  key: string
  label: string
  /** Overrides the library's revision scheme for documents of this type. */
  revisionScheme?: RevisionScheme
}

export interface LibraryControl {
  revisionScheme: RevisionScheme
  /** e.g. "WI-{seq:4}" or "{type}-{yyyy}-{seq:3}". null = numbers are typed by hand. */
  numberPattern: string | null
  documentTypes: DocumentTypeDef[]
  /** Default review interval for new documents; null = no periodic review by default. */
  reviewIntervalMonths: number | null
  /** Days before expiry when the owner and manager are reminded, largest first. */
  reminderDays: number[]
  /** workflow_drafts.id of a workflow that reaffirms a document under review. */
  reviewWorkflowId: string | null
  /** Default document manager: a person or "@Group". */
  documentManager: string | null
}

/** The open periodic review of a document. */
export interface DocumentReview {
  status: 'open' | 'revising'
  openedAt: string
  /** The expiration date the review is for (YYYY-MM-DD). */
  dueDate: string | null
  reason: string | null
  remindersSent: number[]
  overdueNotifiedAt: string | null
  /** The review workflow run, when one was started. */
  runId: string | null
}

export interface SapLink {
  kind: 'material' | 'dir'
  material?: string
  dirType?: string
  dirNumber?: string
  dirPart?: string
  dirVersion?: string
  /** True only when the SAP check found the object. */
  verified: boolean
  checkedAt: string | null
  /** Why it is unverified (no SAP connection, not found, check failed). */
  checkNote?: string | null
  addedBy: string | null
  addedAt: string
}

export interface ExternalLink {
  url: string
  label: string
  addedBy: string | null
  addedAt: string
}

export interface LibraryFolder {
  id: string
  libraryId: string
  parentId: string | null
  name: string
  sortOrder: number
  /** False: this folder's own list applies to it and everything below it. */
  inheritPermissions: boolean
  permissions: AccessRule[]
  createdBy: string | null
  createdAt: string
  deletedAt: string | null
}

export type DocumentSource =
  | { type: 'upload' }
  | { type: 'workflow'; runId: string; submissionId?: string | null; formId?: string | null; formName?: string | null; fieldKey?: string | null }

/** A change that is waiting on the governing workflow. */
export interface PendingDocumentChange {
  kind: 'status' | 'version'
  runId: string | null
  /** The status the document moves to when the workflow approves. */
  targetStatus: string
  /** For a version change: the version that becomes current on approval. */
  versionNo?: number | null
  requestedBy: string
  requestedAt: string
  note?: string | null
}

export interface LibraryDocument {
  id: string
  libraryId: string
  /** null = at the library root. */
  folderId: string | null
  name: string
  status: string
  currentVersion: number
  sizeBytes: number | null
  contentType: string | null
  source: DocumentSource
  pendingChange: PendingDocumentChange | null
  createdBy: string | null
  createdAt: string
  updatedBy: string | null
  updatedAt: string
  deletedAt: string | null
  /** Marked as controlled (CUI), migration 00535: only the CUI group sees it. */
  cui?: boolean
  // Document control (optional: rows from before 00020, and fixtures, may
  // not carry them; read through the defaults in documentControl.ts)
  documentNumber?: string | null
  title?: string | null
  documentType?: string | null
  /** The revision of the current version (assigned by the revision scheme). */
  revision?: string | null
  /** The revision last released (null until the first release). */
  releasedRevision?: string | null
  owner?: string | null
  /** A person or "@Group". */
  manager?: string | null
  effectiveDate?: string | null
  expirationDate?: string | null
  reviewIntervalMonths?: number | null
  lastReviewedAt?: string | null
  review?: DocumentReview | null
  checkedOutBy?: string | null
  checkedOutAt?: string | null
  checkoutNote?: string | null
  sapLinks?: SapLink[]
  externalLinks?: ExternalLink[]
}

export type VersionState = 'current' | 'pending' | 'rejected' | 'previous'

export interface DocumentVersion {
  id: string
  documentId: string
  versionNo: number
  filename: string
  storageContainer: string
  storagePath: string
  sizeBytes: number | null
  contentType: string | null
  note: string | null
  /** The revision label this version carries, when the library assigns them. */
  revision?: string | null
  /** As stored: 'current' | 'pending' | 'rejected'. An older 'current' row
   *  reads as 'previous' once a later version is the document's current one. */
  state: 'current' | 'pending' | 'rejected'
  uploadedBy: string | null
  uploadedAt: string
}

export interface LibraryEvent {
  id: string
  libraryId: string
  folderId: string | null
  documentId: string | null
  actor: string | null
  ts: string
  kind: string
  payload: Record<string, unknown>
}

/** A folder in the starter structure a designer lays out before the library
 *  exists (the form element's settings, the "new library" dialog). */
export interface FolderTemplate {
  name: string
  /** Set to give this folder its own access list instead of inheriting. */
  permissions?: AccessRule[]
  children?: FolderTemplate[]
}

// ---------------------------------------------------------------------------
// Normalization: everything read from jsonb passes through here.
// ---------------------------------------------------------------------------

const COLORS: StatusColor[] = ['gray', 'blue', 'amber', 'green', 'purple', 'red']

export function normalizeRole(v: unknown): LibraryRole {
  return v === 'manager' || v === 'contributor' ? v : 'viewer'
}

export function normalizeAccessRules(raw: unknown): AccessRule[] {
  if (!Array.isArray(raw)) return []
  const out: AccessRule[] = []
  const seen = new Set<string>()
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue
    const principal = String((r as { principal?: unknown }).principal ?? '').trim()
    if (!principal) continue
    const key = principal.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ principal, role: normalizeRole((r as { role?: unknown }).role) })
  }
  return out
}

/** A machine key from a label: "In review" -> "in_review". */
export function statusKeyFromLabel(label: string): string {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'status'
}

/**
 * A usable status scheme whatever was stored: at least one status, unique
 * keys, exactly one initial status. An empty or unreadable scheme falls back
 * to the default lifecycle.
 */
export function normalizeStatuses(raw: unknown): LibraryStatus[] {
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_STATUSES.map((s) => ({ ...s }))
  const out: LibraryStatus[] = []
  const seen = new Set<string>()
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue
    const o = r as Record<string, unknown>
    const label = String(o.label ?? '').trim()
    let key = String(o.key ?? '').trim() || statusKeyFromLabel(label)
    if (!key || !label) continue
    while (seen.has(key)) key = `${key}_2`
    seen.add(key)
    out.push({
      key,
      label,
      color: COLORS.includes(o.color as StatusColor) ? (o.color as StatusColor) : 'gray',
      ...(o.initial === true ? { initial: true } : {}),
      ...(o.released === true ? { released: true } : {}),
      ...(o.retired === true ? { retired: true } : {}),
      ...(o.controlled === true ? { controlled: true } : {}),
    })
  }
  if (out.length === 0) return DEFAULT_STATUSES.map((s) => ({ ...s }))
  const initial = out.findIndex((s) => s.initial)
  out.forEach((s, i) => { if (i === (initial === -1 ? 0 : initial)) s.initial = true; else delete s.initial })
  return out
}

/** A file extension, lower case, no dot ('' when the name has none). */
export function extensionOf(filename: string): string {
  const m = /\.([a-zA-Z0-9]{1,12})$/.exec(filename.trim())
  return m ? m[1]!.toLowerCase() : ''
}
